// Beneficiary detail (§4.1). Shown on the right of the wide layout and as its
// own page on narrow screens — Edit and Delete are always visible (§17.1).
import { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { Beneficiary, BeneficiaryAccount, CountryRef } from '@/types/phase2.types';
import AccountStatusChip from './AccountStatusChip';
import { KIND_ORDER, KIND_TITLES, detailRows } from './beneficiaryDisplay';

interface BeneficiaryDetailProps {
  beneficiary: Beneficiary;
  countries: CountryRef[];
  onEdit: (beneficiary: Beneficiary) => void;
  onDelete: (beneficiary: Beneficiary) => void;
}

const holderNameOf = (account: BeneficiaryAccount): string => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER':
      return account.holderName;
    case 'LOCAL_TRANSFER':
      return account.accountName;
    case 'INTERNATIONAL':
      return account.accountName;
  }
};

const BeneficiaryDetail = ({ beneficiary, countries, onEdit, onDelete }: BeneficiaryDetailProps) => {
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const groups = KIND_ORDER.map((kind) => ({
    kind,
    accounts: beneficiary.accounts.filter((a) => a.kind === kind),
  })).filter((g) => g.accounts.length > 0);

  return (
    <div className="nova-ben-detail">
      <div className="nova-ben-detail-header">
        <h3 className="nova-ben-detail-alias">{beneficiary.alias}</h3>
        <div className="nova-ben-detail-actions">
          <button
            type="button"
            className="nova-icon-btn"
            title="Edit"
            aria-label="Edit beneficiary"
            onClick={() => onEdit(beneficiary)}
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button
            type="button"
            className="nova-icon-btn nova-icon-btn-danger"
            title="Delete"
            aria-label="Delete beneficiary"
            onClick={() => onDelete(beneficiary)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>

      <p className="nova-ben-contact-label">Contact person</p>
      <p className="nova-ben-contact-name">{beneficiary.alias}</p>

      <Accordion defaultActiveKey={groups.map((g) => g.kind)} alwaysOpen className="nova-ben-accordion">
        {groups.map((group) => (
          <Accordion.Item eventKey={group.kind} key={group.kind}>
            <Accordion.Header>{KIND_TITLES[group.kind]}</Accordion.Header>
            <Accordion.Body>
              {group.accounts.map((account) => (
                <div className="nova-ben-account" key={account.id}>
                  <div className="nova-ben-account-head">
                    <span className="nova-ben-account-name">{holderNameOf(account)}</span>
                    <AccountStatusChip status={account.status} />
                  </div>
                  <dl className="nova-ben-rows">
                    {detailRows(account, countries).map((row, i) => {
                      const key = `${account.id}:${i}`;
                      const isRevealed = revealed.has(key);
                      return (
                        <div className="nova-ben-row" key={key}>
                          <dt>{row.label}</dt>
                          <dd>
                            {row.secret ? (
                              <span className="nova-ben-secret">
                                <span>{isRevealed ? row.fullValue ?? row.value : row.value}</span>
                                <button
                                  type="button"
                                  className="nova-ben-eye"
                                  onClick={() => toggle(key)}
                                  aria-label={isRevealed ? `Hide ${row.label}` : `Show ${row.label}`}
                                >
                                  <FontAwesomeIcon icon={isRevealed ? faEyeSlash : faEye} />
                                </button>
                              </span>
                            ) : (
                              row.value
                            )}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              ))}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default BeneficiaryDetail;
